/* oxlint-disable no-console -- structured JSON logging to console */
import {
  emitOtlpStructuredLog,
  getDiagnosticLogFields,
} from '../../telemetry/infrastructure/driven/openTelemetrySetup.ts';

/** JSON console logs: timestamp, user, correlationId (links OTel traces); with OTLP, also forwards logs to the collector. */
export type StructuredUserSnapshot = {
  id?: string;
  email?: string;
  role?: string;
} | null;

export class StructuredLogger {
  private user: StructuredUserSnapshot = null;
  /** Sets user context. */


  setUserContext(user: StructuredUserSnapshot): void {
    this.user = user
      ? { id: user.id, email: user.email, role: user.role }
      : null;
  }
  /** Envelope. */


  private envelope(correlationId: string | undefined, fields: Record<string, unknown>): Record<string, unknown> {
    return {
      timestamp: new Date().toISOString(),
      correlationId: correlationId ?? undefined,
      user: this.user,
      pageUrl: globalThis.location?.href,
      ...getDiagnosticLogFields(),
      ...fields,
    };
  }
  /** Info. */


  info(message: string, context?: Record<string, unknown>): void {
    const cid = typeof context?.correlationId === 'string' ? context.correlationId : undefined;
    const { correlationId: _c, ...rest } = context ?? {};
    const payload = this.envelope(cid, { message, ...rest });
    console.info('[Observability:info]', JSON.stringify(payload));
    emitOtlpStructuredLog('INFO', message, payload);
  }
  /** Error. */


  error(error: Error | string, context?: Record<string, unknown>): void {
    const cid = typeof context?.correlationId === 'string' ? context.correlationId : undefined;
    const { correlationId: _c, ...rest } = context ?? {};
    const err =
      error instanceof Error
        ? { errorName: error.name, errorMessage: error.message }
        : { errorMessage: String(error) };
    const payload = this.envelope(cid, { ...err, ...rest });
    if (error instanceof Error) {
      console.error('[Observability:error]', JSON.stringify(payload), '\n', error);
    } else {
      console.error('[Observability:error]', JSON.stringify(payload));
    }
    const body =
      error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    emitOtlpStructuredLog('ERROR', body, payload);
  }
  /** Event. */


  event(eventName: string, payload?: Record<string, unknown>): void {
    const env = this.envelope(undefined, { eventName, payload });
    console.debug('[Observability:event]', JSON.stringify(env));
    emitOtlpStructuredLog('DEBUG', `event:${eventName}`, env);
  }
  /** Http request. */


  httpRequest(meta: { correlationId: string; method: string; url: string }): void {
    const env = this.envelope(meta.correlationId, { type: 'http.request', ...meta });
    console.info('[Observability:http:request]', JSON.stringify(env));
    emitOtlpStructuredLog('INFO', `${meta.method} ${meta.url}`, env);
  }
  /** Http response. */


  httpResponse(meta: { correlationId: string; status: number; durationMs: number; url: string }): void {
    const env = this.envelope(meta.correlationId, { type: 'http.response', ...meta });
    console.info('[Observability:http:response]', JSON.stringify(env));
    emitOtlpStructuredLog(
      'INFO',
      `HTTP ${meta.status} ${meta.durationMs}ms ${meta.url}`,
      env
    );
  }
}
